import * as React from 'react'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'

import Grid from '@mui/material/Grid'

import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Dialog from '@mui/material/Dialog'
import LoadingButton from '@mui/lab/LoadingButton'
import Link from '@mui/material/Link'

import { renderImportOptionsMenu } from './SubImportMenuItem'

import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import StepButton from '@mui/material/StepButton'

import Typography from '@mui/material/Typography'

import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import FlashOnIcon from '@mui/icons-material/FlashOn'
import LayersIcon from '@mui/icons-material/Layers'
import EditableSelect from '../EditableSelect'
import TextField from '@mui/material/TextField'

import { useZustand } from '../../hooks/useZustand'
import {
    useContextsStore,
    selectOwnedContexts,
} from '../../stores/useContextsStore'
import usePageInfo from '../../hooks/usePageInfo'

import { UserContext } from '../../UserContext'

import { useTheme } from '@mui/material/styles'

import { SubImportContext } from './SubImportContext'
import { DialogsContext } from '../../DialogsContext'

import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import FormHelperText from '@mui/material/FormHelperText'
import FormControlLabel from '@mui/material/FormControlLabel'

import Switch from '@mui/material/Switch'
import Chip from '@mui/material/Chip'
import Box from '@mui/material/Box'
import LinearProgress from '@mui/material/LinearProgress'
import Collapse from '@mui/material/Collapse'
import InputAdornment from '@mui/material/InputAdornment'

import axios from 'axios'
import qs from 'qs'

import { slugifyString } from '../../utils/index'
import { generateTextImportItems } from './TextImportItems'
import { chipHoverAccent } from '../../theme'
import { importErrorMessage } from './importError'
import { refuseNewGraphAtCap } from '../Paywall/refuseNewGraphAtCap'

const addPrivateStatementsSelector = state => state.addPrivateStatementsToStore

const steps = ['Start', 'Sources', 'Query', 'Settings', 'Results']

export default function GoogleImportDialog({
    presetContext = '',
    presetSearchString = '',
    open,
    onClose,
}) {
    const theme = useTheme()

    const { label, location } = usePageInfo()

    const {
        activeSubImportDialog,
        setActiveSubImportDialog,
    } = React.useContext(SubImportContext)

    const {
        activeImportDialog,
        setActiveImportDialog,
        backLinkDialog,
        openImportDialog,
        closeImportDialog,
        setPresetSearchString,
    } = React.useContext(DialogsContext)

    // Shared across all import sources so the typed query persists when switching
    const searchString = presetSearchString
    const setSearchString = setPresetSearchString

    const [selectedContext, setSelectedContext] = React.useState(presetContext)

    const [errorMessage, setErrorMessage] = React.useState(null)

    const [importError, setImportError] = React.useState(null)

    const [completedSteps, setCompletedSteps] = React.useState([])

    const [visitedContext, setVisitedContext] = React.useState(false)

    const [numberOfResults, setNumberOfResults] = React.useState('40')

    const [searchLanguage, setSearchLanguage] = React.useState('Default')

    const [searchCountry, setSearchCountry] = React.useState('US')

    const [hideSearchTerms, setHideSearchTerms] = React.useState(true)

    const [excludePageTitles, setExcludePageTitles] = React.useState(false)

    const [advancedSettings, setAdvancedSettings] = React.useState(false)

    const [doNotSaveGraph, setDoNotSaveGraph] = React.useState(false)

    const [modifyAnalyzedText, setModifyAnalyzedText] = React.useState('none')

    const [replaceEntities, setReplaceEntities] = React.useState(false)

    const [fromInsideContext, setFromInsideContext] = React.useState(false)

    const [contentToAnalyze, setContentToAnalyze] = React.useState('snippets')

    const [analyzeHeadersOnly, setAnalyzeHeadersOnly] = React.useState(false)

    const [showSearchHelp, setShowSearchHelp] = React.useState(false)

    const [loadingMessageIndex, setLoadingMessageIndex] = React.useState(0)

    const loadingMessages = [
        'extracting the search results',
        'building a knowledge graph',
        'the words are the nodes, the co-occurrences are the edges',
        'applying network analysis metrics for clustering and keyword ranking',
    ]

    const menuItems = generateTextImportItems()

    const filteredMenuItems = menuItems.filter(
        ({ textInput }) =>
            textInput.includes('googleSearch') ||
            textInput.includes('googleKeywords') ||
            textInput.includes(backLinkDialog)
    )

    const { username } = React.useContext(UserContext)
    const addPrivateStatementsToStore = useZustand(addPrivateStatementsSelector)

    React.useEffect(() => {
        const savedAdvancedImport = localStorage.getItem('import_advanced')

        if (savedAdvancedImport === '1') {
            setAdvancedSettings(true)
        }

        const savedLanguage = localStorage.getItem('import_language')

        if (savedLanguage) {
            setSearchLanguage(savedLanguage)
        }

        const savedCountry = localStorage.getItem('import_country')

        if (savedCountry) {
            setSearchCountry(savedCountry)
        }
    }, [])

    React.useEffect(() => {
        localStorage.setItem('import_advanced', advancedSettings ? '1' : '0')
    }, [advancedSettings])

    React.useEffect(() => {
        localStorage.setItem('import_country', searchCountry)
    }, [searchCountry])

    React.useEffect(() => {
        localStorage.setItem('import_language', searchLanguage)
    }, [searchLanguage])

    React.useEffect(() => {
        if (location == 'context' && label) {
            setFromInsideContext(true)
        }
    }, [label])

    React.useEffect(() => {
        if (contentToAnalyze === 'pages') {
            setNumberOfResults('10')
        } else {
            setNumberOfResults('40')
        }
    }, [contentToAnalyze])

    const contexts = useContextsStore(selectOwnedContexts)
    const contextNames = contexts.map(c => c.name)

    const [activeStep, setActiveStep] = React.useState(2)
    const [skipped, setSkipped] = React.useState(new Set())

    React.useEffect(() => {
        if (activeStep !== 4) return
        setLoadingMessageIndex(0)
        const timer = setInterval(() => {
            setLoadingMessageIndex(prev =>
                prev < loadingMessages.length - 1 ? prev + 1 : 0
            )
        }, 4000)
        return () => clearInterval(timer)
    }, [activeStep])

    const [checkingGraphRoom, setCheckingGraphRoom] = React.useState(false)

    const handleNext = async () => {
        if (activeStep === 3 && !doNotSaveGraph) {
            setCheckingGraphRoom(true)
            const refused = await refuseNewGraphAtCap(
                selectedContext,
                contextNames
            )
            setCheckingGraphRoom(false)
            if (refused) return
        }
        let newSkipped = skipped

        setActiveStep(prevActiveStep => prevActiveStep + 1)
        setSkipped(newSkipped)
    }

    const handleBack = () => {
        setActiveStep(prevActiveStep => prevActiveStep - 1)
    }

    const handleStep = step => {
        setActiveStep(step)
    }

    const handleNumberOfResultsChange = event => {
        setNumberOfResults(event.target.value)
    }

    const handleSearchStringChange = event => {
        setSearchString(event.target.value)
    }

    const toggleSuggestion = suggestion => {
        const queries = searchString
            .split(',')
            .map(q => q.trim())
            .filter(q => q.length > 0)
        const index = queries.indexOf(suggestion)
        if (index >= 0) {
            queries.splice(index, 1)
        } else {
            queries.push(suggestion)
        }
        setSearchString(queries.join(', '))
    }

    const isSuggestionActive = suggestion => {
        return searchString
            .split(',')
            .map(q => q.trim())
            .includes(suggestion)
    }

    const handleSearchLanguageChange = event => {
        setSearchLanguage(event.target.value)
    }

    const handleSearchCountryChange = event => {
        setSearchCountry(event.target.value)
    }

    React.useEffect(() => {
        if (activeSubImportDialog.type) {
            setActiveStep(2)
            setActiveImportDialog(activeSubImportDialog.type)
        }
    }, [activeSubImportDialog])

    React.useEffect(() => {
        switch (activeStep) {
            case 0: // Start
                setActiveSubImportDialog(false)
                setActiveImportDialog('import')
                break
            case 1: // Sources
                setActiveSubImportDialog(false)
                setActiveImportDialog('shortTextImport')
                break
            case 2: // Query
                break
            case 3: // Settings
                if (!presetContext) {
                    const generatedContextName = generatePresetContext(
                        searchString
                    )
                    const contextNameToUse = contextNames.includes(
                        generatedContextName
                    )
                        ? `${generatedContextName}_1`
                        : generatedContextName
                    setSelectedContext(contextNameToUse)
                }
                break
            case 4: // Results
                if (!searchString) {
                    setErrorMessage(
                        `Please, provide a search query you'd like to get results from Google for.`
                    )
                    setActiveStep(2)
                    break
                }
                if (!selectedContext) {
                    setErrorMessage(
                        'Please, select the graph to import your data to.'
                    )
                    setActiveStep(3)
                    break
                }
                if (selectedContext) {
                    setCompletedSteps([...completedSteps, 4])
                    setErrorMessage(null)
                    submitDataForm()
                    break
                }
        }
    }, [activeStep])

    const getSourceLabel = () => {
        const type =
            (activeSubImportDialog && activeSubImportDialog.type) ||
            backLinkDialog ||
            ''
        if (type.includes('youtubeKeywords')) return 'YouTube Keywords'
        if (type.includes('googleKeywords')) return 'Google Related Keywords'
        return 'Google Search Results'
    }

    const submitDataForm = () => {
        const dataToSend = {
            source: 'googlesearch',
            excludesearchquery: hideSearchTerms ? '1' : '',
            excludetitles: excludePageTitles ? '1' : '',
            doNotIncludeCategories: '',
            searchQuery: searchString,
            context: selectedContext,
            limit: numberOfResults,
            importLanguage: searchLanguage === 'Default' ? '' : searchLanguage,
            importCountry: searchCountry,
            settings: 'morphemes',
            origin: 'googleSearchAdvanced',
            contentToAnalyze: contentToAnalyze,
            doNotSaveGraph: doNotSaveGraph,
            modifyAnalyzedText: modifyAnalyzedText,
            replaceEntities: replaceEntities,
            analyzeHeadersOnly: analyzeHeadersOnly,
        }

        const dataAsForm = qs.stringify(dataToSend)

        const submitUrl =
            contentToAnalyze == 'pages'
                ? '/import/google_pages'
                : '/import/google'

        axios
            .post(submitUrl, dataAsForm, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            })
            .then(response => {
                if (response.data.redirectUrl) {
                    window.location.href = response.data.redirectUrl
                } else {
                    if (response.data.error) {
                        setErrorMessage(
                            typeof response.data.error == 'object'
                                ? response.data.error.error ||
                                      JSON.stringify(response.data.error)
                                : response.data.error
                        )
                        setImportError(response.data.error)
                        setActiveStep(3)
                    }
                    if (response.data.entryuid) {
                        const postedby = username
                        const res = response.data

                        if (doNotSaveGraph && res.statements) {
                            const entryuids = JSON.parse(res.entryuid)
                            const statementHashtags =
                                res.statementHashtags || {}
                            addPrivateStatementsToStore(
                                res.statements,
                                selectedContext,
                                entryuids,
                                statementHashtags
                            )
                        }

                        if (
                            window.myEventEmitter &&
                            myEventEmitter._events['chatMessage']
                        ) {
                            myEventEmitter.emit('chatMessage', {
                                source: null,
                                postedby: postedby,
                                entryuid: JSON.parse(res.entryuid),
                                entrytext: JSON.parse(res.entrytext),
                                entryname: JSON.parse(res.entryname),
                                tags: JSON.parse(res.tags),
                                timestamp: JSON.parse(res.timestamp),
                                graph: res.graph,
                                stimulateVariability: false,
                                message: res.message,
                                statementHashtags: res.statementHashtags || {},
                                doNotSaveGraph: doNotSaveGraph,
                            })
                            closeImportDialog()
                        }
                    }
                }
            })
            .catch(error => {
                console.error('Received the following error', error)
                setErrorMessage(importErrorMessage(error))
                setImportError(error)
                setActiveStep(3)
            })
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth={true}
                maxWidth="lg"
            >
                <DialogTitle
                    sx={{
                        padding: 4,
                        color: theme => theme.palette.text.primary,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    {'Analyze Google search results or related keywords'}
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={onClose}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <Stepper
                    activeStep={activeStep}
                    sx={{ paddingLeft: 4, paddingRight: 4, paddingTop: 2 }}
                >
                    {steps.map((label, index) => {
                        const stepProps = {}
                        const labelProps = {}

                        return (
                            <Step key={label} {...stepProps}>
                                <StepButton
                                    onClick={() => handleStep(index)}
                                    completed={(
                                        completedSteps.indexOf(index) >= 0
                                    ).toString()}
                                >
                                    <StepLabel
                                        {...labelProps}
                                        sx={{
                                            '& .MuiStepLabel-label': {
                                                fontSize: {
                                                    xs: '0.75rem',
                                                    sm: 'initial',
                                                },
                                            },
                                        }}
                                    >
                                        {label}
                                    </StepLabel>
                                </StepButton>
                            </Step>
                        )
                    })}
                </Stepper>

                {errorMessage && (
                    <Typography
                        sx={{
                            paddingRight: 4,
                            paddingLeft: 4,
                            paddingTop: 4,
                            paddingBottom: 0,
                        }}
                        color="error"
                        component="div"
                        gutterBottom
                    >
                        {errorMessage}
                    </Typography>
                )}

                {activeStep === 2 && (
                    <>
                        <DialogContent
                            sx={{
                                paddingRight: 4,
                                paddingLeft: 4,
                                paddingTop: 6,
                            }}
                        >
                            <DialogContentText>
                                We'll analyze results for these queries:
                            </DialogContentText>

                            <FormControl fullWidth sx={{ marginTop: 2 }}>
                                <TextField
                                    label={
                                        'Google search query (comma-separated for multiple)'
                                    }
                                    value={searchString}
                                    variant="outlined"
                                    onChange={handleSearchStringChange}
                                    aria-describedby="searchString-helperText"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderWidth: 2,
                                            '& fieldset': {
                                                borderWidth: 2,
                                                borderColor: 'info.main',
                                            },
                                            '&:hover fieldset': {
                                                borderWidth: 2,
                                                borderColor: 'info.dark',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderWidth: 2,
                                            },
                                        },
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() =>
                                                        setShowSearchHelp(
                                                            !showSearchHelp
                                                        )
                                                    }
                                                    edge="end"
                                                    size="small"
                                                    sx={{
                                                        color: showSearchHelp
                                                            ? 'info.main'
                                                            : 'text.secondary',
                                                    }}
                                                >
                                                    <HelpOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <Collapse in={showSearchHelp}>
                                    <FormHelperText
                                        id="searchString-helperText"
                                        sx={{ marginTop: 2 }}
                                    >
                                        For single queries, you can also use
                                        Google's search operators, such as AND,
                                        OR, "phrase search", as well as
                                        site:noduslabs.com to search a specific
                                        website only or after:2021-03-21 to
                                        filter by time period
                                    </FormHelperText>
                                </Collapse>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        mt: 2.5,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ fontWeight: 500 }}
                                    >
                                        try one of these:
                                    </Typography>
                                    {[
                                        'ai agents',
                                        'ai automation',
                                        'competitive intelligence',
                                        'nvidia',
                                        'solar panels',
                                    ].map(suggestion => {
                                        const active = isSuggestionActive(
                                            suggestion
                                        )
                                        return (
                                            <Chip
                                                key={suggestion}
                                                label={suggestion}
                                                size="medium"
                                                variant={
                                                    active
                                                        ? 'filled'
                                                        : 'outlined'
                                                }
                                                onClick={() =>
                                                    toggleSuggestion(suggestion)
                                                }
                                                sx={{
                                                    cursor: 'pointer',
                                                    fontSize: '0.85rem',
                                                    fontWeight: 500,
                                                    borderColor:
                                                        'rgba(0, 200, 200, 0.5)',
                                                    color: active
                                                        ? 'info.contrastText'
                                                        : 'info.main',
                                                    bgcolor: active
                                                        ? 'info.main'
                                                        : 'transparent',
                                                    '&:hover': {
                                                        bgcolor: active
                                                            ? 'info.dark'
                                                            : 'action.hover',
                                                        color: theme =>
                                                            active
                                                                ? theme.palette
                                                                      .info
                                                                      .contrastText
                                                                : chipHoverAccent(
                                                                      theme
                                                                  ),
                                                        borderColor: theme =>
                                                            active
                                                                ? theme.palette
                                                                      .info.main
                                                                : chipHoverAccent(
                                                                      theme
                                                                  ),
                                                    },
                                                }}
                                            />
                                        )
                                    })}
                                </Box>
                            </FormControl>

                            <FormControl fullWidth sx={{ marginTop: 6 }}>
                                <DialogContentText>
                                    Using the import app:
                                </DialogContentText>
                                <DialogContent
                                    sx={{
                                        pl: 0,
                                        ml: 0,
                                        marginTop: 0,
                                    }}
                                >
                                    {renderImportOptionsMenu(
                                        filteredMenuItems,
                                        'google'
                                    )}
                                </DialogContent>
                            </FormControl>
                        </DialogContent>
                    </>
                )}

                {activeStep === 3 && (
                    <>
                        <DialogContent
                            sx={{
                                paddingRight: 4,
                                paddingLeft: 4,
                                paddingTop: 4,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 4,
                                    py: 1,
                                    px: 2,
                                    bgcolor: 'action.hover',
                                    borderRadius: 1,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    analyzing {getSourceLabel()}
                                </Typography>
                                <ArrowForwardIcon
                                    sx={{
                                        fontSize: 14,
                                        color: 'text.secondary',
                                    }}
                                />
                                <Link
                                    component="button"
                                    variant="caption"
                                    underline="always"
                                    onClick={() => setActiveStep(2)}
                                    sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        cursor: 'pointer',
                                        color: 'info.main',
                                        position: 'relative',
                                        zIndex: 1,
                                    }}
                                >
                                    &ldquo;{searchString}&rdquo;
                                </Link>
                            </Box>

                            <Grid container spacing={8}>
                                <Grid item xs={12} sm={7}>
                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                        {[
                                            {
                                                value: 'snippets',
                                                label:
                                                    'Search Results Snippets',
                                                sub:
                                                    'Best for SEO & topical overview',
                                                icon: <FlashOnIcon />,
                                            },
                                            {
                                                value: 'pages',
                                                label: 'Full Pages',
                                                sub:
                                                    'Deep insights for competitive analysis',
                                                icon: <LayersIcon />,
                                            },
                                        ].map(option => (
                                            <Grid
                                                item
                                                xs={6}
                                                key={option.value}
                                            >
                                                <Box
                                                    onClick={() =>
                                                        setContentToAnalyze(
                                                            option.value
                                                        )
                                                    }
                                                    sx={{
                                                        p: 2.5,
                                                        border: '1px solid',
                                                        borderColor:
                                                            contentToAnalyze ===
                                                            option.value
                                                                ? 'info.main'
                                                                : 'divider',
                                                        borderRadius: 2,
                                                        cursor: 'pointer',
                                                        bgcolor:
                                                            contentToAnalyze ===
                                                            option.value
                                                                ? 'rgba(0, 200, 200, 0.08)'
                                                                : 'transparent',
                                                        transition: 'all 0.2s',
                                                        '&:hover': {
                                                            borderColor:
                                                                'info.main',
                                                        },
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            color:
                                                                contentToAnalyze ===
                                                                option.value
                                                                    ? 'info.main'
                                                                    : 'text.secondary',
                                                            mb: 1,
                                                        }}
                                                    >
                                                        {option.icon}
                                                    </Box>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {option.label}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {option.sub}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Grid>

                                <Grid
                                    item
                                    xs={12}
                                    sm={5}
                                    sx={{
                                        marginBottom: 4,
                                    }}
                                >
                                    <FormControl
                                        fullWidth
                                        sx={{
                                            marginBottom: 3,
                                        }}
                                    >
                                        <EditableSelect
                                            selectItems={contextNames}
                                            label={'Graph name'}
                                            initialValue={selectedContext}
                                            onChange={name =>
                                                setSelectedContext(name)
                                            }
                                        />
                                    </FormControl>

                                    <FormControl
                                        fullWidth
                                        sx={{
                                            marginTop: 2,
                                            marginBottom: 2,
                                        }}
                                    >
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={advancedSettings}
                                                    onChange={event =>
                                                        setAdvancedSettings(
                                                            event.target.checked
                                                        )
                                                    }
                                                />
                                            }
                                            label="Show advanced settings"
                                        />
                                    </FormControl>
                                    <Collapse in={advancedSettings}>
                                        <FormControl
                                            fullWidth
                                            sx={{
                                                marginTop: 2,
                                                marginBottom: 2,
                                            }}
                                        >
                                            <InputLabel id="searchLanguage-label">
                                                Language of the search results
                                            </InputLabel>
                                            <Select
                                                labelId="searchLanguage-label"
                                                id="searchLanguage"
                                                value={searchLanguage}
                                                onChange={
                                                    handleSearchLanguageChange
                                                }
                                                label="Language of the search results"
                                            >
                                                <MenuItem value={'Default'}>
                                                    Default Language
                                                </MenuItem>
                                                <MenuItem value={'EN'}>
                                                    English
                                                </MenuItem>
                                                <MenuItem value={'AR'}>
                                                    Arabic
                                                </MenuItem>
                                                <MenuItem value={'CN'}>
                                                    Chinese (Simplified)
                                                </MenuItem>
                                                <MenuItem value={'TW'}>
                                                    Chinese (Traditional)
                                                </MenuItem>
                                                <MenuItem value={'NL'}>
                                                    Dutch
                                                </MenuItem>
                                                <MenuItem value={'FR'}>
                                                    French
                                                </MenuItem>
                                                <MenuItem value={'DE'}>
                                                    German
                                                </MenuItem>
                                                <MenuItem value={'HE'}>
                                                    Hebrew
                                                </MenuItem>
                                                <MenuItem value={'IT'}>
                                                    Italian
                                                </MenuItem>
                                                <MenuItem value={'JP'}>
                                                    Japanese
                                                </MenuItem>
                                                <MenuItem value={'KO'}>
                                                    Korean
                                                </MenuItem>
                                                <MenuItem value={'NO'}>
                                                    Norwegian
                                                </MenuItem>
                                                <MenuItem value={'FA'}>
                                                    Persian
                                                </MenuItem>
                                                <MenuItem value={'PT'}>
                                                    Portugese
                                                </MenuItem>
                                                <MenuItem value={'RU'}>
                                                    Russian
                                                </MenuItem>
                                                <MenuItem value={'ES'}>
                                                    Spanish
                                                </MenuItem>
                                                <MenuItem value={'SV'}>
                                                    Swedish
                                                </MenuItem>
                                            </Select>
                                        </FormControl>

                                        <FormControl
                                            fullWidth
                                            sx={{
                                                marginTop: 2,
                                                marginBottom: 2,
                                            }}
                                        >
                                            <InputLabel id="searchCountry-label">
                                                For users located in
                                            </InputLabel>
                                            <Select
                                                labelId="searchCountry-label"
                                                id="searchCountry"
                                                value={searchCountry}
                                                onChange={
                                                    handleSearchCountryChange
                                                }
                                                label="For users located in"
                                            >
                                                <MenuItem value={'US'}>
                                                    USA
                                                </MenuItem>
                                                <MenuItem value={'AR'}>
                                                    Argentina
                                                </MenuItem>
                                                <MenuItem value={'AU'}>
                                                    Australia
                                                </MenuItem>
                                                <MenuItem value={'BR'}>
                                                    Brazil
                                                </MenuItem>
                                                <MenuItem value={'CA'}>
                                                    Canada
                                                </MenuItem>
                                                <MenuItem value={'CN'}>
                                                    China
                                                </MenuItem>
                                                <MenuItem value={'FR'}>
                                                    France
                                                </MenuItem>
                                                <MenuItem value={'EG'}>
                                                    Egypt
                                                </MenuItem>
                                                <MenuItem value={'DE'}>
                                                    Germany
                                                </MenuItem>
                                                <MenuItem value={'GB'}>
                                                    Great Britain
                                                </MenuItem>
                                                <MenuItem value={'HK'}>
                                                    Hong-Kong
                                                </MenuItem>
                                                <MenuItem value={'IN'}>
                                                    India
                                                </MenuItem>
                                                <MenuItem value={'IR'}>
                                                    Iran
                                                </MenuItem>
                                                <MenuItem value={'IL'}>
                                                    Israel
                                                </MenuItem>
                                                <MenuItem value={'IT'}>
                                                    Italy
                                                </MenuItem>
                                                <MenuItem value={'JP'}>
                                                    Japan
                                                </MenuItem>
                                                <MenuItem value={'KR'}>
                                                    Korea
                                                </MenuItem>
                                                <MenuItem value={'MX'}>
                                                    Mexico
                                                </MenuItem>
                                                <MenuItem value={'NL'}>
                                                    Netherlands
                                                </MenuItem>
                                                <MenuItem value={'NZ'}>
                                                    New Zealand
                                                </MenuItem>
                                                <MenuItem value={'NO'}>
                                                    Norway
                                                </MenuItem>
                                                <MenuItem value={'PT'}>
                                                    Portugal
                                                </MenuItem>
                                                <MenuItem value={'RU'}>
                                                    Russia
                                                </MenuItem>
                                                <MenuItem value={'SA'}>
                                                    Saudi Arabia
                                                </MenuItem>
                                                <MenuItem value={'SG'}>
                                                    Singapore
                                                </MenuItem>
                                                <MenuItem value={'ES'}>
                                                    Spain
                                                </MenuItem>
                                                <MenuItem value={'SV'}>
                                                    Sweden
                                                </MenuItem>
                                                <MenuItem value={'TW'}>
                                                    Taiwan
                                                </MenuItem>
                                                <MenuItem value={'AE'}>
                                                    UAE
                                                </MenuItem>
                                                <MenuItem value={'CH'}>
                                                    Switzerland
                                                </MenuItem>
                                                <MenuItem value={'GB'}>
                                                    United Kingdom
                                                </MenuItem>
                                            </Select>
                                        </FormControl>

                                        <FormControl
                                            fullWidth
                                            sx={{
                                                marginTop: 2,
                                                marginBottom: 2,
                                            }}
                                        >
                                            <InputLabel id="numberOfResults-label">
                                                Number of results to analyze
                                            </InputLabel>
                                            {contentToAnalyze !== 'pages' && (
                                                <Select
                                                    labelId="numberOfResults-label"
                                                    id="numberOfResults"
                                                    value={numberOfResults}
                                                    onChange={
                                                        handleNumberOfResultsChange
                                                    }
                                                    label="Number of results to analyze"
                                                >
                                                    <MenuItem value={'10'}>
                                                        first page (10 results)
                                                    </MenuItem>
                                                    <MenuItem value={'20'}>
                                                        first 2 pages (20
                                                        results)
                                                    </MenuItem>
                                                    <MenuItem value={'30'}>
                                                        first 3 pages (30
                                                        results)
                                                    </MenuItem>
                                                    <MenuItem value={'40'}>
                                                        first 4 pages (40
                                                        results)
                                                    </MenuItem>
                                                    <MenuItem value={'60'}>
                                                        first 6 pages (60
                                                        results)
                                                    </MenuItem>
                                                    <MenuItem value={'80'}>
                                                        first 8 pages (80
                                                        results)
                                                    </MenuItem>
                                                    <MenuItem value={'100'}>
                                                        first 10 pages (100
                                                        results)
                                                    </MenuItem>
                                                </Select>
                                            )}
                                            {contentToAnalyze === 'pages' && (
                                                <Select
                                                    labelId="numberOfResults-label"
                                                    id="numberOfResults"
                                                    value={numberOfResults}
                                                    onChange={
                                                        handleNumberOfResultsChange
                                                    }
                                                    label="Number of results to analyze"
                                                >
                                                    <MenuItem value={'10'}>
                                                        first page (10 results)
                                                    </MenuItem>
                                                    <MenuItem value={'20'}>
                                                        first 2 pages (20
                                                        results)
                                                    </MenuItem>
                                                </Select>
                                            )}
                                        </FormControl>

                                        <FormControl
                                            fullWidth
                                            sx={{
                                                marginTop: 2,
                                                marginBottom: 2,
                                            }}
                                        >
                                            <InputLabel id="modifyAnalyzedText-label">
                                                Build a knowledge graph from
                                            </InputLabel>
                                            <Select
                                                labelId="modifyAnalyzedText-label"
                                                id="modifyAnalyzedText"
                                                value={modifyAnalyzedText}
                                                onChange={event =>
                                                    setModifyAnalyzedText(
                                                        event.target.value
                                                    )
                                                }
                                                label="Build a knowledge graph from"
                                            >
                                                <MenuItem
                                                    value={'none'}
                                                    key="1"
                                                >
                                                    the words used in text
                                                </MenuItem>
                                                <MenuItem
                                                    value={'detectEntities'}
                                                    key="2"
                                                >
                                                    the words and detected
                                                    entities
                                                </MenuItem>
                                                <MenuItem
                                                    value={
                                                        'extractEntitiesOnly'
                                                    }
                                                    key="3"
                                                >
                                                    only the detected entities
                                                </MenuItem>
                                            </Select>
                                            {modifyAnalyzedText != 'none' && (
                                                <FormHelperText
                                                    sx={{
                                                        marginTop: 1,
                                                    }}
                                                >
                                                    By default, we extract the
                                                    words used in your text. You
                                                    can also choose to detect
                                                    and tag entities in your
                                                    text and build a graph from
                                                    them.
                                                </FormHelperText>
                                            )}
                                        </FormControl>
                                        {modifyAnalyzedText != 'none' && (
                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 2,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            id="replaceEntities"
                                                            checked={
                                                                replaceEntities
                                                            }
                                                            onChange={event =>
                                                                setReplaceEntities(
                                                                    event.target
                                                                        .checked
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label="Replace entities with root concepts"
                                                />
                                                {replaceEntities && (
                                                    <FormHelperText
                                                        id="replaceEntities-helperText"
                                                        sx={{
                                                            marginTop: 1,
                                                        }}
                                                    >
                                                        When turned on, we will
                                                        not only detect the
                                                        entities, but also
                                                        replace them with their
                                                        ontological root
                                                        concepts throughout your
                                                        text.
                                                    </FormHelperText>
                                                )}

                                                <FormHelperText>
                                                    explore the context around
                                                    the search query
                                                </FormHelperText>
                                            </FormControl>
                                        )}

                                        {contentToAnalyze == 'pages' && (
                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 4,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={
                                                                analyzeHeadersOnly
                                                            }
                                                            onChange={event =>
                                                                setAnalyzeHeadersOnly(
                                                                    event.target
                                                                        .checked
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label="Analyze headers only"
                                                />

                                                <FormHelperText>
                                                    extract only
                                                    {
                                                        ' <h1> <h2> <h3> <h4> '
                                                    }{' '}
                                                    tags content
                                                </FormHelperText>
                                            </FormControl>
                                        )}

                                        <FormControl
                                            fullWidth
                                            sx={{
                                                marginTop: 2,
                                                marginBottom: 2,
                                            }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={
                                                            hideSearchTerms
                                                        }
                                                        onChange={event =>
                                                            setHideSearchTerms(
                                                                event.target
                                                                    .checked
                                                            )
                                                        }
                                                    />
                                                }
                                                label="Hide search terms from graph"
                                            />
                                            <FormHelperText>
                                                explore the context around the
                                                search query
                                            </FormHelperText>
                                        </FormControl>

                                        <FormControl
                                            fullWidth
                                            sx={{
                                                marginTop: 2,
                                                marginBottom: 2,
                                            }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={
                                                            excludePageTitles
                                                        }
                                                        onChange={event =>
                                                            setExcludePageTitles(
                                                                event.target
                                                                    .checked
                                                            )
                                                        }
                                                    />
                                                }
                                                label="Exclude page titles"
                                            />
                                            <FormHelperText>
                                                analyze only the Google search
                                                snippets
                                            </FormHelperText>
                                        </FormControl>

                                        {contentToAnalyze == 'pages' && (
                                            <FormControl
                                                fullWidth
                                                sx={{
                                                    marginTop: 2,
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={
                                                                analyzeHeadersOnly
                                                            }
                                                            onChange={event =>
                                                                setAnalyzeHeadersOnly(
                                                                    event.target
                                                                        .checked
                                                                )
                                                            }
                                                        />
                                                    }
                                                    label="Analyze headers only"
                                                />
                                                <FormHelperText>
                                                    extract only
                                                    {
                                                        ' <h1> <h2> <h3> <h4> '
                                                    }{' '}
                                                    tags content
                                                </FormHelperText>
                                            </FormControl>
                                        )}
                                    </Collapse>
                                </Grid>
                            </Grid>
                        </DialogContent>
                    </>
                )}

                {activeStep === 4 && (
                    <>
                        <DialogContent
                            sx={{
                                paddingRight: 4,
                                paddingLeft: 4,
                                paddingTop: 4,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: 350,
                                    py: 6,
                                }}
                            >
                                <Typography
                                    variant="h5"
                                    sx={{
                                        mb: 2,
                                        fontWeight: 400,
                                        fontStyle: 'italic',
                                    }}
                                >
                                    Building your graph...
                                </Typography>
                                <Box sx={{ width: '50%', mb: 4, mt: 2 }}>
                                    <LinearProgress
                                        variant="indeterminate"
                                        sx={{
                                            height: 6,
                                            borderRadius: 3,
                                            bgcolor: 'action.hover',
                                            '& .MuiLinearProgress-bar': {
                                                borderRadius: 3,
                                            },
                                        }}
                                    />
                                </Box>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        minHeight: 24,
                                        transition: 'opacity 0.5s ease',
                                    }}
                                    key={loadingMessageIndex}
                                >
                                    {loadingMessages[loadingMessageIndex]}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ mt: 6 }}
                                >
                                    If this does not complete in 120 seconds,
                                    you can find the graph on{' '}
                                    <Link
                                        href={`/${username}/${selectedContext}/google`}
                                        underline="hover"
                                    >
                                        this page
                                    </Link>
                                </Typography>
                            </Box>
                        </DialogContent>
                    </>
                )}
                <DialogActions
                    sx={{
                        paddingRight: 4,
                        paddingLeft: 4,
                        paddingBottom: 4,
                        paddingTop: 2,
                    }}
                >
                    <Button
                        color="inherit"
                        variant="outlined"
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        sx={{ mr: 1, px: 3, py: 1, fontSize: '0.95rem' }}
                    >
                        Back
                    </Button>

                    <LoadingButton
                        color="info"
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        loading={checkingGraphRoom}
                        disabled={!searchString.length > 0 || activeStep == 4}
                        onClick={handleNext}
                        sx={{ px: 4, py: 1, fontSize: '0.95rem' }}
                    >
                        {activeStep === 3 ? 'Visualize' : 'Next'}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    )
}

function generatePresetContext(searchString) {
    const dateAndTime = new Date()
        .toISOString()
        .slice(2, 16)
        .replace(/-/g, '')
        .replace(/:/g, '')

    if (!searchString) return `google_${dateAndTime}`

    const twoFirstSearchQueryWords = slugifyString(
        searchString
            .split(' ')
            .slice(0, 3)
            .join('_')
            .slice(0, 20)
            .replace(/[^\P{L}a-z][^a-z]*/giu, '')
    )

    return `google_${twoFirstSearchQueryWords}`
}
